import React, { useEffect, useRef, useState } from "react";
import { ArrowUp, Loader2, Square } from "lucide-react";
import ChatMessage from "./ChatMessage";
import ModelSelect from "../ModelSelect";
import { AttachButton, AttachmentTray } from "../compose/Attachments";
import SettingsSheet, { FAST_MODEL } from "./SettingsSheet";
import AgentStatus, { ViewPreviewButton } from "./AgentStatus";
import NotifyPrompt from "../notify/NotifyPrompt";
import MicButton from "../compose/MicButton";
import { useAttachments } from "../../hooks/useAttachments";
import { useVoiceInput } from "../../hooks/useVoiceInput";
import { useCredits } from "../../context/CreditsContext";

const SUGGESTIONS = ["Make it more colourful", "Add a contact form", "Improve the mobile layout", "Add an about page"];

const StopButton = ({ onStop, stopping }) => (
  <button type="button" onClick={onStop} disabled={stopping} data-testid="workspace-stop-build-button" aria-label="Stop building"
    className="h-9 px-3.5 rounded-full bg-rose-500 text-white text-xs font-semibold flex items-center gap-1.5 hover:bg-rose-600 disabled:opacity-60 transition-colors">
    {stopping ? <Loader2 size={13} className="animate-spin" /> : <Square size={11} fill="currentColor" />} {stopping ? "Stopping" : "Stop"}
  </button>
);

const SendButton = ({ disabled, uploading }) => (
  <button type="submit" disabled={disabled} data-testid="workspace-send-chat-button" aria-label="Send"
    className="w-9 h-9 rounded-full bg-slate-900 text-white flex items-center justify-center hover:bg-slate-700 disabled:bg-slate-100 disabled:text-slate-400 transition-colors">
    {uploading ? <Loader2 size={16} className="animate-spin" /> : <ArrowUp size={17} />}
  </button>
);

const Suggestions = ({ onPick }) => (
  <div className="flex gap-1.5 overflow-x-auto scrollbar-hide pb-2.5">
    {SUGGESTIONS.map((s, i) => (
      <button key={s} onClick={() => onPick(s)} data-testid={`chat-suggestion-${i}`}
        className="shrink-0 px-3 py-1 rounded-full bg-slate-100 text-xs text-slate-600 hover:bg-slate-200 hover:text-slate-900 transition-colors">{s}</button>
    ))}
  </div>
);

export const ChatPanel = ({ messages, busy, stopping, onSend, onStop, model, setModel, onPreviewVersion, alwaysPlan, onAlwaysPlan,
  fast, setFast, hasVersion, onViewPreview, previewPillDesktop, onGithub, onFork }) => {
  const [text, setText] = useState("");
  const [plan, setPlan] = useState(!!alwaysPlan);
  const [ai, setAi] = useState(false);
  const att = useAttachments();
  const voice = useVoiceInput((t) => setText((cur) => (cur ? `${cur.trimEnd()} ${t}` : t)));
  const { costOf } = useCredits();
  const endRef = useRef(null);
  const eff = fast ? FAST_MODEL : model;
  const cost = costOf(eff, plan, ai);
  const last = messages[messages.length - 1];
  const sheet = {
    model, setModel, fast, setFast, plan, ai, setAi, always: alwaysPlan, hasVersion, onGithub, onFork,
    togglePlan: (v) => { setPlan(v); if (!v && alwaysPlan) onAlwaysPlan(false); },
    toggleAlways: (v) => { onAlwaysPlan(v); if (v) setPlan(true); },
  };

  useEffect(() => { setPlan(!!alwaysPlan); }, [alwaysPlan]);
  useEffect(() => { endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" }); }, [messages.length, last?.raw?.length, last?.status]);

  const submit = (e) => {
    e?.preventDefault();
    if (!text.trim() || busy || att.uploading) return;
    voice.stop();
    onSend(text.trim(), { mode: plan ? "plan" : "build", ai_images: ai && !plan, attachments: att.ids });
    setText("");
    att.clear();
    setPlan(!!alwaysPlan);
  };
  const approvePlan = (t) => onSend(t, { mode: "build", ai_images: ai, attachments: [] });

  return (
    <div className="h-full flex flex-col" data-testid="chat-panel">
      <div className={`flex-1 overflow-y-auto thin-scroll px-4 py-5 flex flex-col gap-4 ${hasVersion ? "pb-20 lg:pb-5" : ""} ${hasVersion && previewPillDesktop ? "lg:pb-20" : ""}`} data-testid="chat-messages">
        {messages.map((m) => <ChatMessage key={m.id} msg={m} latest={m.id === last?.id} busy={busy} onPreviewVersion={onPreviewVersion} onApprovePlan={approvePlan} />)}
        <div ref={endRef} />
      </div>
      <div className="relative bg-white border-t border-slate-200">
        {hasVersion && <div className="absolute inset-x-0 bottom-full"><ViewPreviewButton onClick={onViewPreview} desktop={previewPillDesktop} /></div>}
        {busy && <NotifyPrompt where="build" />}
        <AgentStatus busy={busy} stopping={stopping} last={last} />
        <div className="p-3">
        {!busy && messages.length > 0 && <Suggestions onPick={setText} />}
        <form onSubmit={submit} onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); att.add(e.dataTransfer.files); }}
          className="rounded-3xl bg-white ring-1 ring-slate-200 shadow-[0_8px_24px_-12px_rgba(14,165,233,0.35)] focus-within:ring-sky-300 transition-shadow">
          <textarea value={text} onChange={(e) => setText(e.target.value)} onPaste={att.onPaste}
            onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) submit(e); }}
            placeholder={busy ? "Ishukart is building… press Stop to interrupt" : "Ask for a change, or paste a screenshot…"}
            rows={2} data-testid="workspace-chat-input"
            className="w-full resize-none bg-transparent px-4 pt-3.5 text-sm text-slate-800 placeholder:text-slate-400 outline-none" />
          <div className="px-3"><AttachmentTray items={att.items} onRemove={att.remove} testid="chat-attachment" /></div>
          <div className="flex items-center justify-between gap-1.5 px-2 pb-2">
            <div className="flex items-center gap-0.5 min-w-0">
              <AttachButton onFiles={att.add} disabled={att.items.length >= 4} testid="chat-attach-button" />
              <ModelSelect value={eff} onChange={(m) => { setModel(m); setFast(false); }} testid="workspace-model-selector" variant="ghost" />
            </div>
            <div className="flex items-center gap-1">
              {cost != null && !busy && <span className="text-[11px] font-mono text-slate-500 mr-0.5" data-testid="chat-cost">{fast ? "fast · " : ""}{plan ? "plan · " : ""}{cost}cr</span>}
              <SettingsSheet s={sheet} testid="chat-settings" />
              <MicButton voice={voice} testid="chat-mic-button" />
              {busy ? <StopButton onStop={onStop} stopping={stopping} /> : <SendButton disabled={!text.trim() || att.uploading} uploading={att.uploading} />}
            </div>
          </div>
        </form>
        </div>
      </div>
    </div>
  );
};

export default ChatPanel;
